import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import api from '../../services/api';

export const UnsubscribePage: React.FC = () => {
  const { t } = useTranslation('newsletter');
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');

  const [email, setEmail] = useState(emailFromUrl || '');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Auto-unsubscribe if email is provided in URL
  useEffect(() => {
    if (emailFromUrl) {
      handleUnsubscribe(emailFromUrl);
    }
  }, [emailFromUrl]);

  const handleUnsubscribe = async (emailToUnsubscribe?: string) => {
    const targetEmail = emailToUnsubscribe || email;

    if (!targetEmail) {
      setStatus('error');
      setMessage(t('unsubscribe.error_no_email'));
      return;
    }

    setStatus('loading');

    try {
      const response = await api.post('/api/newsletter/unsubscribe', {
        email: targetEmail,
      });

      setStatus('success');
      setMessage(response.data.message || t('unsubscribe.success_message'));
    } catch (error: any) {
      setStatus('error');
      setMessage(error.response?.data?.message || t('unsubscribe.error_failed'));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleUnsubscribe();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('unsubscribe.title')}
          </h1>
          <p className="text-gray-600">
            {t('unsubscribe.description')}
          </p>
        </div>

        {status === 'success' ? (
          <Alert variant="success" className="mb-4">
            <div className="text-center">
              <p className="font-semibold mb-2">{message}</p>
              <p className="text-sm">
                {t('unsubscribe.success_message')}
              </p>
              <a
                href="/"
                className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('unsubscribe.return_home')}
              </a>
            </div>
          </Alert>
        ) : (
          <>
            {status === 'error' && (
              <Alert variant="error" className="mb-4" onClose={() => setStatus('idle')}>
                {message}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('unsubscribe.email_label')}
                type="email"
                placeholder={t('unsubscribe.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'loading'}
              />

              <Button
                type="submit"
                variant="danger"
                className="w-full"
                disabled={status === 'loading'}
                loading={status === 'loading'}
              >
                {status === 'loading' ? t('unsubscribe.button_unsubscribing') : t('unsubscribe.button_unsubscribe')}
              </Button>

              <p className="text-sm text-gray-500 text-center mt-4">
                {t('unsubscribe.changed_mind')}{' '}
                <a href="/" className="text-primary-600 hover:text-primary-700 font-medium">
                  {t('unsubscribe.go_back')}
                </a>
              </p>
            </form>
          </>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {t('unsubscribe.support_text')}{' '}
            <a href="/contact" className="text-primary-600 hover:text-primary-700">
              {t('unsubscribe.contact_support')}
            </a>
            .
          </p>
        </div>
      </Card>
    </div>
  );
};

export default UnsubscribePage;
